// Personalization Service for tracking user behavior and preferences

class PersonalizationService {
  constructor() {
    this.MAX_RECENTLY_VIEWED = 12;
    this.MAX_HISTORY_ITEMS = 50;
    this.STORAGE_KEY = 'user_preferences';
    this.preferences = this.loadPreferences();
    this.detectDevice();
    this.detectSeason();
    this.detectLocation();
  }

  // Load preferences from localStorage
  loadPreferences() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Convert favoriteCategories back to Map
        if (parsed.favoriteCategories) {
          parsed.favoriteCategories = new Map(Object.entries(parsed.favoriteCategories));
        }
        return parsed;
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    }

    return {
      recentlyViewed: [],
      browsingHistory: [],
      favoriteCategories: new Map(),
      device: { type: 'desktop' },
    };
  }

  // Save preferences to localStorage
  savePreferences() {
    // Convert Map to object for storage
    const toStore = {
      ...this.preferences,
      favoriteCategories: Object.fromEntries(this.preferences.favoriteCategories),
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(toStore));
  }

  // Track a viewed product
  trackProductView(product) {
    // Add to browsing history
    this.preferences.browsingHistory.unshift({
      productId: product.id,
      timestamp: Date.now(),
      category: product.category,
    });

    // Trim history if too long
    if (this.preferences.browsingHistory.length > this.MAX_HISTORY_ITEMS) {
      this.preferences.browsingHistory = this.preferences.browsingHistory.slice(0, this.MAX_HISTORY_ITEMS);
    }

    // Update recently viewed (avoid duplicates)
    const filtered = this.preferences.recentlyViewed.filter(p => p.id !== product.id);
    this.preferences.recentlyViewed = [product, ...filtered].slice(0, this.MAX_RECENTLY_VIEWED);

    // Update favorite categories
    const categoryCount = this.preferences.favoriteCategories.get(product.category) || 0;
    this.preferences.favoriteCategories.set(product.category, categoryCount + 1);

    this.savePreferences();
  }

  // Get recently viewed products
  getRecentlyViewed(excludeId) {
    if (excludeId) {
      return this.preferences.recentlyViewed.filter(p => p.id !== excludeId);
    }
    return this.preferences.recentlyViewed;
  }

  // Get smart recommendations based on browsing history
  getSmartRecommendations(currentProduct, allProducts = []) {
    const recommendations = [];
    const recommendationSet = new Set();

    // If we have a current product, prioritize same category
    if (currentProduct) {
      const sameCategory = allProducts.filter(p =>
        p.category === currentProduct.category &&
        p.id !== currentProduct.id &&
        !recommendationSet.has(p.id)
      );

      // Add up to 4 from same category
      sameCategory.slice(0, 4).forEach(p => {
        recommendations.push(p);
        recommendationSet.add(p.id);
      });
    }

    // Add products from favorite categories
    const sortedCategories = Array.from(this.preferences.favoriteCategories.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category]) => category);

    for (const category of sortedCategories) {
      const categoryProducts = allProducts.filter(p =>
        p.category === category &&
        !recommendationSet.has(p.id) &&
        p.id !== currentProduct?.id
      );

      categoryProducts.slice(0, 2).forEach(p => {
        if (recommendations.length < 8) {
          recommendations.push(p);
          recommendationSet.add(p.id);
        }
      });
    }

    // Add seasonal recommendations
    const seasonalProducts = this.getSeasonalProducts(allProducts).filter(p =>
      !recommendationSet.has(p.id) &&
      p.id !== currentProduct?.id
    );

    seasonalProducts.slice(0, 4).forEach(p => {
      if (recommendations.length < 12) {
        recommendations.push(p);
        recommendationSet.add(p.id);
      }
    });

    return recommendations;
  }

  // Get seasonal products
  getSeasonalProducts(products) {
    const season = this.preferences.season;
    const seasonalKeywords = {
      summer: ['shorts', 'training', 'light', 'breathable', 'cool'],
      winter: ['jacket', 'long sleeve', 'warm', 'thermal'],
      spring: ['jersey', 'new', 'fresh'],
      autumn: ['hoodie', 'training', 'layer'],
    };

    if (!season) return [];

    const keywords = seasonalKeywords[season] || [];
    return products.filter(p => {
      const searchText = `${p.name} ${p.description || ''}`.toLowerCase();
      return keywords.some(keyword => searchText.includes(keyword));
    });
  }

  // Detect device type
  detectDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone/i.test(userAgent);
    const isTablet = /tablet|ipad/i.test(userAgent);

    this.preferences.device = {
      type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
      os: /android/i.test(userAgent) ? 'android' : /iphone|ipad|mac/i.test(userAgent) ? 'ios' : 'other',
    };
  }

  // Detect current season based on date
  detectSeason() {
    const month = new Date().getMonth() + 1;

    if (month >= 3 && month <= 5) {
      this.preferences.season = 'spring';
    } else if (month >= 6 && month <= 8) {
      this.preferences.season = 'summer';
    } else if (month >= 9 && month <= 11) {
      this.preferences.season = 'autumn';
    } else {
      this.preferences.season = 'winter';
    }
  }

  // Detect user location (basic, would need geolocation API for accuracy)
  detectLocation() {
    try {
      // Try to get location from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone.includes('Nairobi') || timezone.includes('Africa')) {
        this.preferences.location = {
          country: 'Kenya',
          region: 'East Africa',
        };
      }
    } catch (error) {
      console.error('Error detecting location:', error);
    }
  }

  // Get location-based recommendations
  getLocationBasedContent() {
    const isKenya = this.preferences.location?.country === 'Kenya';

    return {
      currency: isKenya ? 'KSh' : '$',
      shippingMessage: isKenya
        ? 'Free shipping on orders over KSh 5,000 within Nairobi!'
        : 'International shipping available',
      popularCategories: isKenya
        ? ['National Team Jerseys', 'Local Clubs', 'Premier League']
        : ['International Teams', 'Champions League', 'World Cup'],
    };
  }

  // Get device-specific UI settings
  getDeviceSettings() {
    const deviceType = this.preferences.device.type;

    return {
      gridColumns: deviceType === 'mobile' ? 1 : deviceType === 'tablet' ? 2 : 4,
      imageSize: deviceType === 'mobile' ? 'small' : deviceType === 'tablet' ? 'medium' : 'large',
      showQuickView: deviceType === 'desktop',
    };
  }

  // Clear user data (for privacy)
  clearUserData() {
    localStorage.removeItem(this.STORAGE_KEY);
    this.preferences = {
      recentlyViewed: [],
      browsingHistory: [],
      favoriteCategories: new Map(),
      device: { type: 'desktop' },
    };
  }

  // Get personalized greeting
  getPersonalizedGreeting() {
    const hour = new Date().getHours();
    const season = this.preferences.season;

    let greeting = '';
    if (hour < 12) {
      greeting = 'Good morning';
    } else if (hour < 18) {
      greeting = 'Good afternoon';
    } else {
      greeting = 'Good evening';
    }

    // Add seasonal touch
    if (season === 'summer') {
      greeting += '! ☀️ Check out our summer collection';
    } else if (season === 'winter') {
      greeting += '! ❄️ Stay warm with our latest gear';
    }

    return greeting;
  }
}

// Create singleton instance
export const personalizationService = new PersonalizationService();
